import GameObject, { GameObjectEvent } from 'GameObject/GameObject';
import WorldMap, { SagaMapContainers } from 'Prototype/World/WorldMap';
import Point from 'Utility/Point';
import Game from 'Game';
import LevelNode from 'Prototype/World/LevelNode/LevelNode';
import IWorldSettings from '../../Data/IWorldSettings';
import PlayerIcon from 'Prototype/World/PlayerIcon';
import TransformComponent from 'Component/Transform';
import LeaderboardHelper, { Leaderboard } from '../../../UI/LeaderboardHelper';
import FriendIcon from 'Prototype/World/FriendIcon';
import IBaseWorldData from '../../Data/IBaseWorldData';
import Container from 'Prototype/Container';
import MapComponentHandler from '../Components/MapComponentHandler';
import SagaMapComponent from 'Component/Map/SagaMapComponent';
import AnimationComponent from 'Component/Animation';
import SpriteComponent from 'Component/Render/Sprite';
import { PlayerActions } from "State/Player/PlayerActions";
import { PlayerTask } from "State/Player/PlayerTasks";
import { captureException } from "Sentry";
import { IGameState } from "State/Game/IGameState";
import { IScoreEntry } from "Utility/SDK/Drivers/IScoreEntry";
import { WorldDataHelper } from "Utility/WorldDataHelper";
import { FTUXType } from "Player/PlayerProperty/Progress/FTUXType";
import LevelNodeState from 'Prototype/World/LevelNode/LevelNodeState';
import { questProgressPopUpDelayFull, questProgressPopUpDelay } from "Constants/Quests";
import Pet from 'Prefabs/Pet/Pet';
import { IPetCreateData } from "Prefabs/Pet/IPetCreateData";
import { PetsHelper } from "Utility/Helpers/Pets/PetsHelper";
import { PetAnimations } from "Utility/Helpers/Pets/PetAnimations";
import { PetArmature } from "Utility/Helpers/Pets/PetArmature";
import { RewardType } from "Utility/RewardHelper";
import { PetId } from "Constants/Pets";
import { QuestStateHelper } from "State/Player/Quests/QuestStateHelper";
import { StarChestStateHelper } from 'State/Player/StarChests/StarChestStateHelper';
import { ProgressStateHelper } from 'State/Player/Progress/ProgressStateHelper';
import { FTUXStateHelper } from 'State/Game/FTUX/FTUXStateHelper';
import LuckyWheel from 'Prefabs/LuckyWheel/LuckyWheel';
import { LuckyWheelStateHelper } from 'State/Player/LuckyWheel/LuckyWheelStateHelper';
import Sprite from 'Prototype/Sprite';
import Scene from 'Scene/Scene';
import { getHighestLevelCompleted } from "State/Player/PlayerLevelHelper";
import RenderSystem, { RenderSystemEvent } from 'System/Render/RenderSystem';
import { MathHelper } from 'Utility/MathHelper';
import PIXIRenderer from 'System/Render/PIXIRenderer';

export default class SagaMapHandler {
    public static generateWorld(params: {
        worldNumber: number;
        worldData: IBaseWorldData;
        worldLayout: any;
        rememberToCleanUp: (cleanUp: () => void) => void;
        scene: Scene;
    }): WorldMap {
        const state: IGameState = Game.store.getState();
        /** Star Chest tier to display when world is created.
         * If stars gained from the previous level are enough to upgrade the chest
         * Then the tier below the current tier should be displayed initially
         * This is so an upgrade animation may be shown */
        const starChestTierToDisplay = StarChestStateHelper.getStarChestUnlockTier(
            state.playerState,
            state.worldState.starsGained
        );

        const currentLevelNumber = Game.player.getNextLevelInWorld(params.worldNumber);

        const levelNodes = SagaMapHandler.addLevelNodes({
            starChestTier: starChestTierToDisplay,
            worldData: params.worldData,
            worldLayout: params.worldLayout,
            levelContainer: params.worldLayout.nodesContainer,
        });

        // Finds the correct node to place the player icon on when map loads
        let currentNode: LevelNode;
        try {
            currentNode =
                state.worldState.firstComplete &&
                levelNodes.has(currentLevelNumber - 1) &&
                levelNodes.has(currentLevelNumber + 1)
                    ? levelNodes.get(currentLevelNumber - 1)
                    : levelNodes.get(currentLevelNumber);
        } catch (error) {
            currentNode = levelNodes.get(currentLevelNumber);
        }

        const worldMap: WorldMap = SagaMapHandler.createMap({
            currentLevelPosition: currentNode.getComponent(TransformComponent).position.clone() as Point,
            tileCount: params.worldData.tiles,
            worldNumber: params.worldNumber,
            worldSettings: params.worldData.settings,
            worldLayout: params.worldLayout,
        });

        const handleResize = () => {
            const currentNodePosition = currentNode.getComponent(TransformComponent).position;
            const transform = worldMap.getComponent(TransformComponent);
            const scaleX = transform.scale.x;
            const nodePosX = currentNodePosition.x * scaleX;
            const x = PIXIRenderer.renderer.width / 2 - nodePosX;
            const minX = -WorldMap.Width * scaleX + PIXIRenderer.renderer.width;
            transform.x = MathHelper.clamp(x, minX, 0);
        };
        handleResize();

        RenderSystem.on(RenderSystemEvent.RESIZE, handleResize);

        worldMap.getComponent<SagaMapComponent>(SagaMapComponent).levelNodes = levelNodes;

        // Create map components
        const mapComponentsContainer: Container = GameObject.Instantiate(Container, {
            name: SagaMapContainers.MAP_COMPONENTS_CONTAINER,
        });
        const components: GameObject[] = MapComponentHandler.addMapComponents(
            params.worldData.components,
            currentLevelNumber,
            params.scene
        );

        const signBubblesContainer = GameObject.Instantiate(Container, {
            name: SagaMapContainers.SIGN_BUBBLES_CONTAINER,
        });
        worldMap.addChild(signBubblesContainer);

        components.forEach(component => mapComponentsContainer.addChild(component));
        worldMap.addChild(mapComponentsContainer);

        const playerIcon: GameObject = SagaMapHandler.addPlayerIcon(params.worldNumber, currentNode);
        worldMap.addChild(playerIcon);

        // Add container for friend icons
        const friendIconContainer: Container = GameObject.Instantiate(Container, {
            name: SagaMapContainers.FRIEND_ICON_CONTAINER,
        });
        const timeout = setTimeout(() => {
            SagaMapHandler.addFriendIcons(levelNodes, friendIconContainer, currentLevelNumber);
        }, 2500);
        params.rememberToCleanUp.call(null, () => {
            clearTimeout(timeout);
        });

        worldMap.addChild(friendIconContainer);

        const currentLevel: number = Game.player.getNextLevelInWorld(
            params.worldNumber
        );

        addLuckyWheel();

        addPet();

        // TODO: For tests only, remove when thumbnails are implemented
        // TODO: Uncomment this to test items unlocked popup
        // if(state.worldState.firstLoad) {
            // UIActions.showPopup(PopupID.FARM_UNLOCK_ITEMS, {});
        // }

        if (ProgressStateHelper.getLevelStars(state, currentLevel) === 0) {
            let autoShowFlowPopup = !state.worldState.firstLoad;
            if (state.worldState.firstComplete) {
                // Queue an animation task to jump to next node
                PlayerActions.addTasks([
                    {
                        task: PlayerTask.ICON_HOP,
                        levelNumber: currentLevelNumber,
                    },
                ]);

                const highestLevelCompleted: number = Game.player.getHighestLevelCompleted(state);
                const fieldData = WorldDataHelper.getFieldData(params.worldNumber);
                if (
                    state.playerState.fields.length > 0 &&
                    fieldData.some(field => field.requiredLevel.includes(highestLevelCompleted))
                ) {
                    autoShowFlowPopup = false;
                }
            } else if (!state.worldState.firstLoad) {
                autoShowFlowPopup = false;
            }

            if (
                autoShowFlowPopup &&
                (currentLevel === state.playerState.petLevel ||
                    !QuestStateHelper.canCompleteAnyQuest(state.playerState))
                    && FTUXStateHelper.isFtuxCompleted(state.playerState, FTUXType.HARVEST)
            ) {
                const questsTasks = state.playerState.tasks.filter(
                    taskData => taskData.task === PlayerTask.SHOW_QUEST_PROGRESS
                );
                const starChestTask = state.playerState.tasks.find(
                    taskData => taskData.task === PlayerTask.STAR_CHEST_STAR_REWARD
                );
                const questDelayIncrement = starChestTask ? questProgressPopUpDelay : questProgressPopUpDelayFull;
                const preLevelPopupDelay = 300 + questDelayIncrement * questsTasks.length;
                PlayerActions.showLevelAutoFlowPopup({
                    delay: preLevelPopupDelay,
                    levelNumber: currentLevel,
                });
            }
        }

        return worldMap;

        function addLuckyWheel() {
            const maxWorldLevel = WorldDataHelper.maxLevel(state.worldState.worldNumber);
            const highestLevelCompleted = getHighestLevelCompleted(state.playerState);
            const luckyWheelNodes = LuckyWheelStateHelper.getEligibleWheelLevels(state.playerState.lastClaimedSpinLevel, maxWorldLevel, highestLevelCompleted);
            const petLevel = state.worldState.petSpawnLevel ?? state.playerState.petLevel;

            const container = GameObject.Instantiate(Container, {
                name: SagaMapContainers.LUCKY_WHEEL_CONTAINER,
            });

            luckyWheelNodes.forEach(levelNumber => {
                const isPetLevel = petLevel === levelNumber;
                const luckyWheelNode = levelNodes.get(levelNumber);
                if (luckyWheelNode) {
                    const luckyWheel = GameObject.Instantiate(LuckyWheel, {
                        name: `luckyWheel_${levelNumber}`,
                        position: luckyWheelNode.getWheelPosition(),
                        scale: new Point(0.8, 0.8),
                        buttonMode: currentLevel === levelNumber,
                        visible:
                            !isPetLevel
                            && FTUXStateHelper.isFtuxCompleted(state.playerState, FTUXType.LUCKY_WHEEL)
                            && levelNumber > state.playerState.lastClaimedSpinLevel,
                    });

                    container.addChild(luckyWheel);

                    if (isPetLevel && state.playerState.tasks.find(task => task.task === PlayerTask.PET_WALK)) {
                        PlayerActions.addTasks([
                            {
                                task: PlayerTask.SHOW_WHEEL,
                                luckyWheel,
                            },
                        ]);
                    }
                }
            });
            worldMap.addChild(container);

        }

        function addPet() {
            const activePet = state.playerState.petsState.activePet;
            const petMeta: IPetCreateData = {
                petId: activePet,
                armatureBlacklist: [PetArmature.DIGGING, PetArmature.MINI_GAME_FED, PetArmature.MINI_GAME_HUNGRY, PetArmature.MY_FARM],
                isFtuxCompleted: FTUXStateHelper.isFtuxCompleted(state.playerState, FTUXType.PET_UNLOCK),
            };

            const petNode = levelNodes.get(state.worldState.petSpawnLevel ?? state.playerState.petLevel);
            if (activePet && petNode) {
                const petData = PetsHelper.getPetData(activePet);
                petMeta.position = Point.add(petNode.getPetPosition(), petData.positionOffset);
                petMeta.scale = petData.nodeScale;
            }

            const pet = GameObject.Instantiate(Pet, {
                name: 'pet',
                ...petMeta,
            });

            // TODO: Add if checking if user already did the ftux for pet
            pet.initialized?.then(() => pet.play(PetAnimations.IDLE));

            const container = GameObject.Instantiate(Container, {
                name: SagaMapContainers.PET_CONTAINER,
            });

            worldMap.addChild(container);
            container.addChild(pet);

            if (!PetsHelper.hasUnlockedAPet(state.playerState) && !state.playerState.petsState.petInDailyLogin) {
                PlayerActions.receiveRewards({
                    rewards: [
                        {
                            type: RewardType.PET,
                            value: 1,
                            petId: PetId.Charly,
                        },
                    ],
                });
            }
        }
    }

    protected static createMap(params: {
        currentLevelPosition: Point;
        tileCount: number;
        worldNumber: number;
        worldSettings: IWorldSettings;
        worldLayout: any;
    }): WorldMap {
        const map = GameObject.Instantiate(WorldMap, {
            // World settings to assign map orientation and correctly place background tiles
            worldSettings: params.worldSettings,
            worldLayout: params.worldLayout,
            tileCount: params.tileCount,
        });
        return map;
    }

    private static addLevelNodes(props: {
        worldLayout: any;
        worldData: IBaseWorldData;
        levelContainer: GameObject;
        starChestTier: number;
    }): Map<number, LevelNode> {
        const levelNodes: Map<number, LevelNode> = new Map<number, LevelNode>();
        const state: IGameState = Game.store.getState();
        const starChestFTUXComplete = FTUXStateHelper.isFtuxCompleted(state.playerState, FTUXType.STAR_CHEST);

        const limits = props.worldData.limits;
        const nodeSprites = [...props.worldLayout.nodesContainer.children];

        nodeSprites.forEach((baseSprite: Sprite, i) => {
            const levelNumber = limits.min + i;
            const levelNodeState: LevelNodeState = LevelNode.getNodeState(levelNumber, state);
            const starChestStarCount = Math.max(
                0,
                StarChestStateHelper.getStarChestStarCount(state.playerState) - (state.worldState.starsGained ?? 0)
            );

            const levelNode: LevelNode = GameObject.Instantiate(LevelNode, {
                baseSprite: baseSprite,
                number: levelNumber,
                levelNodeState,
                stars: ProgressStateHelper.getLevelStars(state, levelNumber),
                cullOffScreen: true,
                cullPadding: [80, 90, 80, 90],
                isStarChestLevel: isStarChestLevel(levelNumber),
                starChestTier: props.starChestTier,
                starChestFTUXComplete,
                starChestStarCount,
                petId: state.playerState.petLevel === levelNumber ? state.playerState.petsState.activePet : null,
            });
            levelNodes.set(levelNumber, levelNode);
            props.levelContainer.addChild(levelNode);
        });

        levelNodes.forEach(node => {
            if (isStarChestLevel(node.levelNumber)) {
                props.levelContainer.addChild(node);
            }
        });

        return levelNodes;

        function isStarChestLevel(levelNumber: number) {
            return levelNumber === state.playerState.starChestLevel;
        }
    }

    private static addPlayerIcon(worldNumber: number, currentLevelNode: LevelNode) {
        const currentLevelNodeTransform: TransformComponent = currentLevelNode.getComponent(TransformComponent);
        const playerIcon = GameObject.Instantiate(PlayerIcon, {
            name: SagaMapContainers.PLAYER_ICON,
            position: currentLevelNodeTransform.position.clone(),
            cullOffScreen: true,
            cullPadding: [200, 100, 100, 100],
            //isClientPlayer:true
        });

        const avatarURL = Game.sdk.getAvatar();

        if (avatarURL) {
            LeaderboardHelper.setPlayerAvatar(
                'player_avatar',
                avatarURL,
                playerIcon.getComponentsInChildren(SpriteComponent).find(s => s.texture === 'no_avatar')
            );
        }

        playerIcon.addComponent(AnimationComponent);

        playerIcon.on(GameObjectEvent.SELECTED, () => {
            PlayerActions.showLevelAutoFlowPopup({
                levelNumber: Game.player.getNextLevelInWorld(worldNumber),
            });
        });

        return playerIcon;
    }
    protected static async addFriendIcons(
        levelNodes: Map<number, LevelNode>,
        container: GameObject,
        playerCurrentLevel: number
    ) {
        const entries: IScoreEntry[] = await Game.sdk.getFriendScores(Leaderboard.TOTAL_LEVELS, 'default');

        await LeaderboardHelper.getPlayerAvatars(entries);

        const friendLevels: number[] = [];
        for (let i = 0, n = entries.length; i < n; i++) {
            const entry: IScoreEntry = entries[i];
            if (entry.user.isCurrentUser) continue;

            // Get how many other friends are on the same node
            const placement: number = friendLevels.filter((friendLevel: number) => friendLevel === entry.score).length;
            friendLevels.push(entry.score);

            const levelNode: LevelNode = levelNodes.get(entry.score);
            if (levelNode && !levelNode.destroyed) {
                const levelNodeTransform: TransformComponent = levelNode.getComponent(TransformComponent);
                // Sentry
                if (levelNodeTransform === undefined) {
                    captureException({
                        message: 'Level node transfrom undefined',
                        node: levelNode,
                        entry: entry,
                    });
                }

                const offset: Point = entry.score === playerCurrentLevel ? new Point(30, 20) : Point.Zero;

                const friendIcon: FriendIcon = GameObject.Instantiate(FriendIcon, {
                    friendId: entry.user.playerId,
                    avatarTexture: 'no_avatar',
                    position: new Point(
                        levelNodeTransform.position.x + 15 * placement + offset.x,
                        levelNodeTransform.position.y + offset.y
                    ),
                    cullOffScreen: true,
                    cullPadding: [100, 100, 100, 100],
                    scale: Point.Zero,
                    placement,
                });

                LeaderboardHelper.getPlayerAvatar(entry).then(key => (friendIcon.avatar = key));

                container.addChild(friendIcon);
            }
        }
    }
    public static loadTiles(worldNumber: number): void {
        const mapKey: string = `world${worldNumber}`;
        if (!Game.resource.existsOrQueued(mapKey)) {
            Game.resource.add(mapKey, `resource/graphics/sprite_sheets/${mapKey}.json`);
        }
    }
}
